/**
 * Shared rules for who appears on the T-Shirt roster and how Firestore allocation is read.
 * Keep in sync with roster construction in TShirtManagement.
 */

/** Sentinel stored in Firestore / UI for explicit no shirt */
export const TSHIRT_NO_ALLOCATION_VALUE = 'NONE';

export function normalizeSize(raw) {
  if (!raw && raw !== 0) return '';
  if (typeof raw === 'object') {
    // Malformed / legacy Firestore values; String(object) becomes "[object Object]" in the roster.
    if (typeof raw.size === 'string' || typeof raw.size === 'number') {
      return normalizeSize(raw.size);
    }
    return '';
  }
  const t = String(raw).trim().toUpperCase();
  if (!t) return '';
  return t;
}

export function getRequestedSize(person) {
  if (!person) return '';
  const fromRequested = normalizeSize(person.tShirtRequested);
  if (fromRequested) return fromRequested;
  const fromLegacy = normalizeSize(person.tShirtSize);
  if (fromLegacy) return fromLegacy;
  return '';
}

export function isWithdrawnOrDuplicate(flyer) {
  if (!flyer) return false;
  if (flyer.duplicate === true) return true;
  const statusText = (flyer.status || '').toString().toLowerCase();
  const notesText = (flyer.notes || '').toString().toLowerCase();
  return (
    statusText.includes('withdrawn') ||
    statusText.includes('duplicate') ||
    statusText.includes('cancelled') ||
    statusText.includes('canceled') ||
    notesText.includes('withdrawn') ||
    notesText.includes('duplicate') ||
    notesText.includes('cancelled') ||
    notesText.includes('canceled')
  );
}

export function isWaitingMentor(flyer) {
  if (!flyer || flyer.type !== 'mentor') return false;
  if (flyer.waiting) return true;
  const st = (flyer.status || '').toString().toLowerCase();
  return st.includes('wait');
}

/** Keep one flyer per email; prefer the one without duplicate in status/notes. */
export function dedupeFlyersByEmail(flyers, isWithdrawnOrDuplicateFn) {
  const byEmail = new Map();
  flyers.forEach((f) => {
    const email = (f.email || '').toString().toLowerCase().trim();
    if (!email) {
      byEmail.set(`__no-email-${f.id}`, f);
      return;
    }
    const existing = byEmail.get(email);
    if (!existing) {
      byEmail.set(email, f);
      return;
    }
    const existingDup = isWithdrawnOrDuplicateFn(existing);
    const currentDup = isWithdrawnOrDuplicateFn(f);
    if (currentDup && !existingDup) return;
    if (!currentDup && existingDup) {
      byEmail.set(email, f);
      return;
    }
    byEmail.set(email, existing);
  });
  return Array.from(byEmail.values());
}

export function getFlyerCreatedAtMs(flyer) {
  const ts = flyer && flyer.createdAt;
  if (!ts) return null;
  try {
    if (typeof ts.toDate === 'function') {
      const d = ts.toDate();
      return d instanceof Date ? d.getTime() : null;
    }
    if (typeof ts.seconds === 'number') {
      return ts.seconds * 1000;
    }
    if (typeof ts === 'number') {
      return ts;
    }
  } catch {
    // ignore
  }
  return null;
}

/** Prefer registration date (from sheet) for early/late cutoff; fallback to createdAt. */
export function getRegistrationDateMs(flyer) {
  const raw = flyer && flyer.registrationDate;
  if (raw != null) {
    try {
      if (typeof raw.toDate === 'function') {
        const d = raw.toDate();
        return d instanceof Date ? d.getTime() : null;
      }
      if (typeof raw.seconds === 'number') return raw.seconds * 1000;
      if (typeof raw === 'number') return Number.isFinite(raw) ? (raw < 1e12 ? raw * 1000 : raw) : null;
      const d = new Date(String(raw));
      return Number.isFinite(d.getTime()) ? d.getTime() : null;
    } catch {
      // fall through
    }
  }
  return getFlyerCreatedAtMs(flyer);
}

export function getCutoffMs(event) {
  const raw = event && event.tShirtPriorityCutoff;
  if (!raw) return null;
  try {
    if (typeof raw.toDate === 'function') {
      const d = raw.toDate();
      return d instanceof Date ? d.getTime() : null;
    }
    if (typeof raw.seconds === 'number') {
      return raw.seconds * 1000;
    }
    if (typeof raw === 'number') {
      return raw;
    }
    const d = new Date(String(raw));
    const ms = d.getTime();
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

/** Sum ordered counts from Firestore tshirtInventory.sizes (or similar plain object). */
export function sumSavedInventoryOrdered(sizesField) {
  if (!sizesField || typeof sizesField !== 'object') return 0;
  return Object.values(sizesField).reduce((sum, val) => {
    const n = Number(val);
    return sum + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
}

/** True when nothing saved yet — not the same as explicit NONE (no shirt by choice). */
export function isFirestoreTshirtTrulyUnallocated(allocatedNormalized) {
  return !normalizeSize(allocatedNormalized);
}

export function isFirestoreTshirtExplicitNone(allocatedNormalized) {
  return normalizeSize(allocatedNormalized) === TSHIRT_NO_ALLOCATION_VALUE;
}

/**
 * Count roster members (same cohort as T-Shirt page) with no tShirtAllocated decision in Firestore.
 * People with explicit NONE are not counted (they are intentionally not receiving a shirt).
 */
export function countFirestoreTshirtUnallocatedRosterMembers(event, flyers) {
  if (!event || !Array.isArray(flyers)) return 0;
  const cutoffMs = getCutoffMs(event);
  const activeFlyers = flyers.filter(
    (f) =>
      f &&
      (f.type === 'freshling' || f.type === 'mentor') &&
      !f.test &&
      !f.cancelled &&
      !isWithdrawnOrDuplicate(f)
  );
  const deduped = dedupeFlyersByEmail(activeFlyers, isWithdrawnOrDuplicate);
  const earlyFreshlings = [];
  const lateFreshlings = [];
  const mentors = [];
  deduped.forEach((f) => {
    if (f.type === 'mentor') {
      mentors.push(f);
    } else if (f.type === 'freshling') {
      const createdMs = getRegistrationDateMs(f);
      if (cutoffMs != null && createdMs != null && createdMs <= cutoffMs) {
        earlyFreshlings.push(f);
      } else {
        lateFreshlings.push(f);
      }
    }
  });

  let count = 0;
  const bumpIfMissing = (doc) => {
    const a = normalizeSize(doc.tShirtAllocated);
    if (isFirestoreTshirtTrulyUnallocated(a)) count += 1;
  };

  earlyFreshlings.forEach(bumpIfMissing);
  lateFreshlings.forEach(bumpIfMissing);
  flyers
    .filter((f) => f && f.type === 'volunteer' && !f.test && !f.cancelled && !isWithdrawnOrDuplicate(f))
    .forEach(bumpIfMissing);
  mentors.filter((f) => !isWaitingMentor(f)).forEach(bumpIfMissing);

  return count;
}
